import { api } from "@/lib/api";

/**
 * PWA + Web Push utilities. iOS Safari 16.4+ ve modern Chrome/Edge/Firefox'ta çalışır.
 * Notification izni kullanıcı etkileşimiyle istenir; engellenmişse sessizce durur.
 */

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
    return reg;
  } catch (e) {
    console.warn("[sw] register failed", e);
    return null;
  }
}

/**
 * Push iznini ister ve subscription'ı backend'e kaydeder.
 * @returns { ok: boolean, reason?: string }
 */
export async function enablePushNotifications() {
  if (!isPushSupported()) return { ok: false, reason: "Tarayıcı bildirim desteklemiyor" };
  if (Notification.permission === "denied") {
    return { ok: false, reason: "Bildirim izni reddedildi — tarayıcı ayarlarından açmanız gerek" };
  }
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") return { ok: false, reason: "Bildirim izni verilmedi" };

  const reg = await navigator.serviceWorker.ready;
  // Sunucudan public VAPID key al
  let publicKey;
  try {
    const { data } = await api.get("/push/public-key");
    publicKey = data.public_key;
  } catch {
    return { ok: false, reason: "Sunucudan anahtar alınamadı" };
  }
  if (!publicKey) return { ok: false, reason: "Push anahtarı yapılandırılmamış" };

  // Mevcut subscription varsa onu kullan, yoksa yeni iste
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  return { ok: true };
}

export async function disablePushNotifications() {
  if (!isPushSupported()) return { ok: true };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const json = sub.toJSON();
      try { await api.post("/push/unsubscribe", { endpoint: json.endpoint, keys: json.keys }); }
      catch (_) { /* sessizce yut — kullanıcı zaten çıkmak istiyor */ }
      await sub.unsubscribe();
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || "Kapatılamadı" };
  }
}

export async function getPushSubscriptionStatus() {
  if (!isPushSupported()) return { supported: false, subscribed: false, permission: "default" };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return {
    supported: true,
    subscribed: !!sub,
    permission: typeof Notification !== "undefined" ? Notification.permission : "default",
  };
}
